import { isMobileDevice } from "./isMobileDevice";
import {
  shouldEnterLiveTrainingAfterPresence,
} from "./liveTrainingSessionWindow";
import {
  getHomeRolesFromUser,
  readActiveHomeRole,
  writeActiveHomeRole,
} from "./homeRole";
import { userCanEditPlanningSession } from "./planningAccess";

const COACH_LT_AUTO_ENTER_SUPPRESS_KEY =
  "smarttt_coach_lt_auto_enter_suppress_v1";

/** Prefer the strongest staff Home role the account actually holds. */
function preferredStaffHomeRole(user) {
  const roles = getHomeRolesFromUser(user);
  if (roles.includes("coach")) return "coach";
  if (roles.includes("monitor")) return "monitor";
  if (roles.includes("admin")) return "admin";
  if (roles.includes("moderator")) return "moderator";
  if (roles.includes("committee")) return "committee";
  return "coach";
}

/**
 * When a staff user opens Live Training from a coach surface while Home is
 * still on "View as player", switch to a staff role so they get coach UI.
 * Pure monitors stay on "monitor" (not forced to "coach").
 * No-op for pure players or when already on a staff home role.
 * @returns {boolean} true if the user can use coach Live Training UI
 */
export function ensureCoachRoleForLiveTrainingEntry(user, permissions) {
  if (!userCanEditPlanningSession(user, permissions)) return false;
  const active = readActiveHomeRole(user);
  if (active === "player") {
    writeActiveHomeRole(preferredStaffHomeRole(user));
  }
  return true;
}

/**
 * Coach manually left Live Training for Home — stop bouncing them back until
 * they intentionally open Live Training again for this session.
 */
export function suppressCoachLiveTrainingAutoEnter(tsId) {
  const id = tsId != null ? String(tsId).trim() : "";
  if (!id || typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(
      COACH_LT_AUTO_ENTER_SUPPRESS_KEY,
      JSON.stringify({ ts_id: id, atMs: Date.now() })
    );
  } catch {
    /* ignore */
  }
}

export function isCoachLiveTrainingAutoEnterSuppressed(tsId) {
  const id = tsId != null ? String(tsId).trim() : "";
  if (!id || typeof sessionStorage === "undefined") return false;
  try {
    const raw = sessionStorage.getItem(COACH_LT_AUTO_ENTER_SUPPRESS_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return String(parsed?.ts_id || "") === id;
  } catch {
    return false;
  }
}

/** Clear after an intentional Live Training entry (or when the session ends). */
export function clearCoachLiveTrainingAutoEnterSuppression(tsId = null) {
  if (typeof sessionStorage === "undefined") return;
  try {
    if (tsId == null || tsId === "") {
      sessionStorage.removeItem(COACH_LT_AUTO_ENTER_SUPPRESS_KEY);
      return;
    }
    const id = String(tsId).trim();
    const raw = sessionStorage.getItem(COACH_LT_AUTO_ENTER_SUPPRESS_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (String(parsed?.ts_id || "") === id) {
      sessionStorage.removeItem(COACH_LT_AUTO_ENTER_SUPPRESS_KEY);
    }
  } catch {
    /* ignore */
  }
}

/**
 * Whether the Training page should hand the coach straight into Live Training
 * (30 min before start / live / paused). Used for auto-redirect — not a CTA.
 */
export function shouldCoachAutoEnterLiveTraining(session) {
  if (!session?.ts_id) return false;
  if (isCoachLiveTrainingAutoEnterSuppressed(session.ts_id)) return false;
  return shouldEnterLiveTrainingAfterPresence(session);
}

/**
 * @deprecated Prefer shouldCoachAutoEnterLiveTraining — explicit LT CTAs removed.
 * Kept for tour/tests that still call the old name.
 */
export function canAccessLiveTrainingFromTrainingPage(
  session,
  { mobile = false } = {}
) {
  if (mobile) return shouldCoachAutoEnterLiveTraining(session) || Boolean(session?.ts_id);
  return shouldCoachAutoEnterLiveTraining(session);
}

/**
 * Resolve navigation target when a player/coach taps a session on mobile.
 * Live Training from 30 minutes before start (same window as presence redirect).
 * @returns {string|null} path or null to use default logic
 */
export function resolveMobileSessionNavPath(session) {
  if (!session || !isMobileDevice()) return null;
  if (shouldEnterLiveTrainingAfterPresence(session)) {
    return "/live-training";
  }
  return null;
}

/** @deprecated use resolveMobileSessionNavPath */
export function resolveLiveTrainingSessionNavPath(session) {
  return resolveMobileSessionNavPath(session);
}
